import React, {Component} from 'react';
import PropTypes from 'prop-types';
import firebase from 'firebase/app';
import 'firebase/database';
import 'firebase/auth';
import {EditorDialog} from '../editor-components';
import RoleOptions from './role-options';

/** @function dotify
  * Helper function to convert emails to a valid
  * firebase uri sytnax
  * @param {string} email - the email to convert
  */
function dotify(email) {
  return email.replace(/\./g, '-dot-');
}

/** @class UserCreationDialog
  * A component for creating a new "pending" user.
  * When a pending user is created, a login link email
  * is sent to thier supplied email address.
  * Pending users are transferred to the "active" user
  * list on thier first successful login.
  * @prop {Array} claimedEmails - a list of emails that belong to
  * already active users
  * @prop {boolean} show - if the dialog should be shown
  * @prop {function} onExit - a callback to hide the dialog
  */
export default class UserCreationDialog extends Component {
  /** @constructor
    * Constructs a new instance of the UserCreationDialog
    */
  constructor(props) {
    super(props);
    this.state = {
      name: '',
      email: '',
      role: 'Unassigned'
    }
    this.validate = this.validate.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.handleCreate = this.handleCreate.bind(this);
  }
  /** @method handleClear
    * Clears the user state and error message
    */
  handleClear() {
    this.setState({
      name: '',
      email: '',
      role: 'Unassigned',
      errorMessage: undefined
    });
  }
  /** @method validate
    * Validates the user creation form fields and sets
    * an error message if an invalid value is found.
    * @return {boolean} true if the data is valid, false otherwise
    */
  validate() {
    if(!this.state.name || this.state.name.length === 0) {
      this.setState({errorMessage: "You must specify a name for the user."});
      return false;
    }
    var re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if(!this.state.email || !re.test(this.state.email.toLowerCase())) {
      this.setState({errorMessage: "The supplied email address does not seem to be valid."});
      return false;
    }
    if(this.props.claimedEmails.includes(this.state.email)) {
      this.setState({errorMessage: "That email belongs to an already active user."});
      return false;
    }
    return true;
  }
  /** @method handleCreate
    * Creates a new pending user in the firebase database
    * and sends them a login email.
    */
  handleCreate(event) {
    event.preventDefault();

    // Validate the user form before moving forward
    if(!this.validate()) return;

    // Create the pending user
    var actionCodeSettings = {
      url: 'https://admin.vexposhows.com/',
      handleCodeInApp: true
    };
    firebase.auth().sendSignInLinkToEmail(this.state.email, actionCodeSettings)
    .then(() => {
      // The link was successfully sent; create the pending user record
      firebase.database().ref(`/pendingUsers/${dotify(this.state.email)}`).set({
        name: this.state.name,
        email: this.state.email,
        role: this.state.role
      }, () => {
        alert(`A login link was sent to ${this.state.name} at ${this.state.email}`);
        this.handleClear();
        this.props.onExit();
      });
    })
    .catch((error) => {
      alert(`There was an error sending a login link to ${this.state.name} at ${this.state.email}: ${error.code}`);
      console.error(error)
    });
  }
  /** @method handleChange
    * Applies user changes to the state
    * @param {InputEvent} event - the triggering event
    */
  handleChange(event) {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;
    this.setState({
      [name]: value
    });
  }
  /** @method render
    * Renders the control
    */
  render() {
    const style = {maxWidth: "600px"}
    return (
      <EditorDialog show={this.props.show} onExit={this.props.onExit}>
        <form style={style} onSubmit={this.handleCreate}>
          <div className="info-panel">
            You can create a new Vexpo Admin Platform
            user and assign thier role using the form below.
            A login link email will be automatically sent to
            the new user.
          </div>
          <h3>Add a New Vexpo User</h3>
          { (this.state.errorMessage) ? (
            <div className="error-panel">
              {this.state.errorMessage}
            </div>
          ): '' }
          <div className="input-group">
            <label htmlFor="name">Name</label>
            <input name="name" type="text"
              value={this.state.name}
              onChange={this.handleChange}
              minLength={1}
              required
              />
          </div>
          <div className="input-group">
            <label htmlFor="email">Email</label>
            <input name="email" type="email"
              value={this.state.email}
              onChange={this.handleChange}
              required
              />
          </div>
          <div className="input-group">
            <label htmlFor="role">Role</label><br/>
            <select name="role"
              value={this.state.role}
              onChange={this.handleChange}
              required
            >
              <RoleOptions/>
            </select>
          </div>
          <div className="button-group">
            <button onClick={this.handleClear} className="btn btn-warning">
              Clear Form
            </button>
            <button className="btn btn-success">
              Create User
            </button>
          </div>
        </form>
      </EditorDialog>
    );
  }
}

UserCreationDialog.propTypes = {
  claimedEmails: PropTypes.array.isRequired,
  show: PropTypes.bool.isRequired,
  onExit: PropTypes.func.isRequired
}



// WEBPACK FOOTER //
// ./src/user-list/user-creation-dialog.js